import 'react-datepicker/dist/react-datepicker.css';
import './DatePicker.scss';
import { isSafari } from 'lib/util';
import React, { useState, useEffect, useRef } from 'react';
import { Form } from 'react-bootstrap';
import CatePicker from 'react-datepicker';
import { pl } from 'date-fns/locale';
import { FaArrowDown, FaArrowUp, FaCalendar } from 'react-icons/fa';
import TimeInput from './TimeInput';
import SmallSpinner from '../ClearableFormControl/SmallSpinner';
import { useDevice } from 'lib/hooks/useDevice';

interface DatePickerProps {
    value: Date | null;
    onChange: (date: Date | null) => void;
    id: string;
    type: string;
    isInvalid?: boolean;
    min?: Date;
    isClearable?: boolean;
    isLoading?: boolean;
    locale?: Locale;
    className?: string;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    shouldCloseOnSelect?: boolean;
}

const DatePicker = (props: DatePickerProps) => {
    const [monthPicker, setMonthPicker] = useState<boolean>(false);
    const [yearPicker, setYearPicker] = useState<boolean>(false);
    const [baseMinDate, setBaseMinDate] = useState<Date | undefined>(props.min);
    const [isCloseOnSelect, setIsCloseOnSelect] = useState<boolean>(true);
    const [spinnerVisible, setSpinnerVisible] = useState<boolean>(false);

    const datepickerRef = useRef<CatePicker>(null);
    const yearsCountOnYearPicker = 12;
    const { isDesktop } = useDevice();

    useEffect(() => {
        if (!props.isLoading) setSpinnerVisible(false);
    }, [props.isLoading]);

    useEffect(() => {
        if (props.min) {
            const minDate = new Date(props.min);
            if (yearPicker) minDate?.setFullYear(props.min.getFullYear() - 1);
            else if (monthPicker) {
                if (props.min.getMonth() === 0) {
                    minDate?.setFullYear(props.min.getMonth() - 1);
                    minDate?.setMonth(11);
                } else {
                    minDate?.setMonth(props.min.getMonth() - 1);
                }
            }
            if (props.value !== null && props.value < minDate) props.onChange(null);
            setBaseMinDate(minDate);
        }
        setIsCloseOnSelect(!(monthPicker || yearPicker || props.type === 'datetime'));
    }, [yearPicker, monthPicker, props.min]);

    const onChangeSave = (date: Date | null, event: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement> | undefined) => {
        if (date === null) return;
        if (yearPicker) {
            setYearPicker(false);
            setMonthPicker(true);
        } else if (monthPicker) {
            setMonthPicker(false);
        } else {
            if (props.isLoading !== undefined && props.value?.toDateString() !== date.toDateString()) setSpinnerVisible(true);
            props.onChange(date);
        }
    };

    const CustomInput = React.forwardRef<HTMLInputElement, any>(
        ({ value, onClick, placeholder }, ref) => (
            <div onClick={onClick} ref={ref}>
                <Form.Control
                    id={props.id}
                    value={value}
                    readOnly
                    autoComplete='off'
                    placeholder={placeholder}
                    isInvalid={props.isInvalid}
                    className='form-control'
                    style={{ backgroundColor: '#fff', cursor: 'default' }}
                />
                {
                    spinnerVisible ?
                        <SmallSpinner /> :
                        <FaCalendar className={`calendar-icon ${props.isInvalid ? 'd-none' : ''}`} />
                }
            </div>
        )
    );

    return (
        <>
            <div className='data-picker-wrapper'>
                <CatePicker
                    id={props.id}
                    ref={datepickerRef}
                    placeholderText={props.type === 'date' ? 'YYYY-MM-DD' : 'YYYY-MM-DD hh:mm'}
                    showTimeInput={props.type === 'datetime'}
                    dateFormat={props.type === 'date' ? 'yyyy-MM-dd' : 'yyyy-MM-dd HH:mm'}
                    selected={props.value}
                    showMonthYearPicker={monthPicker}
                    showYearPicker={yearPicker}
                    shouldCloseOnSelect={isCloseOnSelect}
                    minDate={baseMinDate}
                    onChange={onChangeSave}
                    customInput={<CustomInput />}
                    className={`${props.className ?? ''}`}
                    locale={props.locale ?? pl}
                    fixedHeight
                    adjustDateOnChange={false}
                    withPortal={!isDesktop}
                    yearItemNumber={yearsCountOnYearPicker}
                    onBlur={props.onBlur}
                    customTimeInput={<TimeInput
                        selectedDate={props.value}
                        setSelectedDate={props.onChange}
                        closeCalendar={() => datepickerRef.current?.setOpen(false)}
                    />}
                    renderCustomHeader={({
                        date,
                        changeYear,
                        decreaseMonth,
                        increaseMonth,
                        decreaseYear,
                        increaseYear,
                        prevMonthButtonDisabled,
                        nextMonthButtonDisabled
                    }) => (
                        <div
                            style={{
                                margin: 10,
                                display: 'flex',
                                justifyContent: 'space-between'
                            }}
                        >
                            <div>
                                {!(monthPicker || yearPicker) ? (
                                    <button
                                        type='button'
                                        className='datepicker-header-btn'
                                        onClick={() => { setMonthPicker(true); }}
                                    >
                                        {date.toLocaleString('pl-PL', { month: 'long' })}
                                    </button>
                                ) :
                                    <></>}
                                <button
                                    type='button'
                                    className='datepicker-header-btn'
                                    onClick={() => { setYearPicker(true); }}
                                >
                                    {yearPicker ? `${date.getFullYear() - ((date.getFullYear() % yearsCountOnYearPicker) - 1)} - ${date.getFullYear() + (yearsCountOnYearPicker - (date.getFullYear() % yearsCountOnYearPicker))}` : date.getFullYear()}
                                </button>
                            </div>
                            <div>
                                <button className='datepicker-header-btn' type='button' onClick={() => { if (yearPicker) changeYear(date.getFullYear() - yearsCountOnYearPicker); else if (monthPicker) decreaseYear(); else decreaseMonth(); }} disabled={prevMonthButtonDisabled}>
                                    <FaArrowUp />
                                </button>
                                <button type='button' className='datepicker-header-btn' onClick={() => { if (yearPicker) changeYear(date.getFullYear() + yearsCountOnYearPicker); else if (monthPicker) increaseYear(); else increaseMonth(); }} disabled={nextMonthButtonDisabled}>
                                    <FaArrowDown />
                                </button>
                            </div>
                        </div>
                    )}
                >
                    <div className='datepicker-bottom-buttons-wrapper'>
                        <button type='button' className={`datepicker-button ${props.isClearable ? '' : 'd-none'}`} style={{ float: 'left' }} onClick={() => { props.onChange(null); if (datepickerRef.current !== null) datepickerRef.current.setOpen(false); }}>
                            Wyczyść
                        </button>
                        <button type='button' className={`datepicker-button ${props.min && new Date() < props.min ? 'button-disabled' : ''}`} disabled={props.min && new Date() < props.min} style={{ float: 'right', cursor: 'auto' }} onClick={() => { if (props.isLoading !== undefined && new Date().toDateString() !== props.value?.toDateString()) setSpinnerVisible(true); props.onChange(new Date()); if (datepickerRef.current !== null) datepickerRef.current.setOpen(false); }}>
                            Dzisiaj
                        </button>
                    </div>
                </CatePicker>
            </div>
            {isSafari() && (
                <Form.Control.Feedback type='invalid' tooltip>
                    Użyj formatu YYYY-MM-DD
                </Form.Control.Feedback>
            )}
        </>
    );
};

export default DatePicker;
